import * as React from "react";
import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import SingleAnnotation from "./SingleAnnotation";
import { getToolStateDataItems } from "../utils/annotationFormat";

import { useDispatch, useSelector } from "react-redux";
import RefreshIcon from "@mui/icons-material/Refresh";

export default function SingleFileAnnotations(props) {
  const dicom_data = useSelector((state) => state.dcm);
  const dispatch = useDispatch();

  // Add a local state to track visibility updates
  const [visibilityVersion, setVisibilityVersion] = useState(0);
  const [dataChange, setDataChange] = useState(0);

  // Sample circle measurements data

  useEffect(() => {
    setDataChange((prev) => prev + 1);
  }, [dicom_data]);

  // Update local state when dicom_data.update changes
  useEffect(() => {
    // Increment the version to force a re-render
    setVisibilityVersion((prev) => prev + 1);
  }, [dicom_data.update]);

  const toggleLines = (type) => {
    dispatch({
      type: "ANNOTATION_CHANGE_REQUEST",
      stack_id: props.dcm_key,
      key: props.dcm_type,
      payload: {
        index: null,
        annot_type: "Line",
        action_type: type,
      },
    });
  };

  // Function to toggle handles visibility for all frames
  const toggleHandlesForAllFrames = () => {
    // First toggle for current frame to update Redux state
    toggleLines("toggle_handles");

    // Get the current value to determine what the new value should be
    const currentHandlesState = props.data.annotations.Line?.handles;
    const newHandlesValue = !currentHandlesState;

    // Get stack data to access image IDs
    const stack =
      dicom_data?.dicom_data?.[props.dcm_key]?.[props.dcm_type]?.stack;
    if (!stack || !stack.imageIds) return;

    // Dispatch a custom action to tell OPTViewer to update all frames
    dispatch({
      type: "UPDATE_ALL_FRAMES_HANDLES",
      payload: {
        stack_id: props.dcm_key,
        dcm_type: props.dcm_type,
        imageIds: stack.imageIds,
        handles: newHandlesValue,
      },
    });
  };

  // Helper function to sort AiSegmentation data
  const sortAiSegmentationData = (data) => {
    if (!data || !Array.isArray(data)) return data;

    // Custom sort order for AiSegmentation labels
    const priorityOrder = {
      ILM: 1, // Display ILM first
      BM: 2, // Display BM second
      // Add other labels here with their priority if needed
    };

    return [...data].sort((a, b) => {
      const labelA = a.label || "";
      const labelB = b.label || "";

      // Get priority values (default to 999 if not specified)
      const priorityA = priorityOrder[labelA] || 999;
      const priorityB = priorityOrder[labelB] || 999;

      return priorityA - priorityB;
    });
  };

  return (
    <div>
      <Box
        style={{
          padding: "6px 10px",
          backgroundColor: "rgb(215, 217, 220)",
          fontSize: 15,
          borderBottom: "1px solid #ccc",
        }}
      >
        {props.dcm_type}
      </Box>
      {props.data === undefined || props.data === null ? (
        <h6 style={{ color: "#09f" }}>No annotations available</h6>
      ) : (
        <Box>
          {Object.entries(props.data.annotations).map((key, value) => {
            // Don't render MultiLine annotations
            if (
              key[0] === "MultiLine" ||
              key[0] === "FluidSegmentation" ||
              key[0] === "lisonsAreasData"
            ) {
              return null;
            }

            const toolItems = getToolStateDataItems(key[1]);
            const shouldRenderToolList =
              key[0] !== "Brush" || key[0] === "LisonProbe";

            return key[1] === undefined ||
              !toolItems.length ||
              !shouldRenderToolList ? null : (
              <Box
                key={"annotation-" + key}
                style={{ marginBottom: "0px" }}
                sx={{
                  bgcolor: "rgba(210, 205, 200, 0.2)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    paddingRight: "10px",
                  }}
                >
                  <div style={{ display: "flex", paddingLeft: "4px" }}>
                    {key[0] === "Line" && (
                      <input
                        type="checkbox"
                        checked={key[1].visible}
                        onChange={() => toggleLines("visibility_all")}
                      />
                    )}
                    {key[0] !== "LisonProbe" ? (
                      <h6
                        style={{ margin: 0, fontSize: 14, padding: "6px 10px" }}
                      >
                        {(() => {
                          const getDisplayText = () => {
                            switch (key[0]) {
                              case "AiSegmentation":
                                return "AI-Segm ";
                              case "FluidSegmentation":
                                return "Fluid Segm ";
                              case "Line":
                                return "Native-Segm ";
                              case "MultiPointProbe":
                                return `${
                                  toolItems.length > 1
                                    ? "Lesions "
                                    : "Lesion "
                                } ${
                                  dicom_data.lisonOptions.lisonAreaselected
                                } Area`;
                              case "Lison":
                                return "Lesion ";
                              default:
                                return key[0];
                            }
                          };
                          return getDisplayText();
                        })()}
                        {key[0] !== "FluidSegmentation" &&
                          key[0] !== "MultiLine" &&
                          `(${toolItems.length})`}
                      </h6>
                    ) : null}
                  </div>
                  {key[0] === "Line" && (
                    <div style={{ display: "flex" }}>
                      <RefreshIcon
                        fontSize="small"
                        onClick={() => props.onFetchClick(props.dcm_id)}
                        style={{
                          marginTop: "4px",
                          cursor: "pointer",
                        }}
                      />
                      <span style={{ fontSize: 14, padding: "3px 10px" }}>
                        Handles
                        <input
                          id="handles-checkbox"
                          style={{ marginLeft: "5px", paddingTop: 4 }}
                          type="checkbox"
                          checked={key[1].handles}
                          onChange={toggleHandlesForAllFrames}
                        />
                      </span>
                    </div>
                  )}
                </div>
                {(key[0] === "AiSegmentation"
                  ? sortAiSegmentationData(toolItems)
                  : toolItems
                ).map((annotation, index) => {
                  const originalIndex =
                    key[0] === "AiSegmentation"
                      ? toolItems.findIndex((item) => item === annotation)
                      : index;

                  return (
                    <SingleAnnotation
                      dcm_key={props.dcm_key}
                      dcm_type={props.dcm_type}
                      annot_type={key[0]}
                      key={`annott-${key}-${originalIndex}-${visibilityVersion}-${annotation.visible}`}
                      item={annotation}
                      label={key[0]}
                      index={originalIndex}
                    />
                  );
                })}
              </Box>
            );
          })}

          {/* Add Circle Measurements at the end */}
        </Box>
      )}
    </div>
  );
}
